/**
 * Content-free error reports from the app (pre-launch F8). When something throws that nobody handled, one small report goes to
 * the app's own server (`/api/diagnostics/client-error`, worker/monitoring.ts): what kind of failure, the error's name, a
 * scrubbed message, where in the code, and which build. Never a note, a title, an address, an account or a device identifier.
 * At most a few per page load, each failure once. Disclosed in the privacy policy ("Technical logs").
 */
export interface ClientErrorReport {
  kind: 'error' | 'unhandledrejection'
  name: string
  message: string
  frame: string
  build: string
  surface: 'web' | 'android'
}

const MAX_REPORTS_PER_LOAD = 5

/** Quoted text can be a piece of a note ("Unexpected token 'h', "hello…" is not valid JSON"); addresses and emails are personal. */
export function scrubMessage(text: unknown): string {
  return String(text ?? '')
    .replace(/(["'`“‘])[^"'`”’]*\1/g, '$1…$1')
    .replace(/https?:\/\/\S+/gi, '<url>')
    .replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '<email>')
    .replace(/\d{6,}/g, '<n>')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 160)
}

/** The first stack frame that is in our code, as `file:line:column` without the origin or the query string. */
export function codeFrame(stack: unknown): string {
  for (const line of String(stack ?? '').split('\n').slice(0, 12)) {
    const match = /([A-Za-z0-9_.-]+\.(?:js|mjs|ts|tsx)):(\d+):(\d+)/.exec(line)
    if (match) return `${match[1]}:${match[2]}:${match[3]}`
  }
  return ''
}

export function buildClientErrorReport(kind: ClientErrorReport['kind'], reason: unknown, context: { build: string; surface: ClientErrorReport['surface'] }): ClientErrorReport {
  const error = reason instanceof Error ? reason : null
  return {
    kind,
    name: (error?.name ?? 'NonError').replace(/[^A-Za-z0-9_.$ -]/g, '').slice(0, 60) || 'Error',
    message: scrubMessage(error ? error.message : reason),
    frame: codeFrame(error?.stack),
    build: context.build,
    surface: context.surface,
  }
}

export type SendReport = (report: ClientErrorReport) => void

export function beaconSender(endpoint: string): SendReport {
  return report => {
    const body = JSON.stringify(report)
    try {
      if (typeof navigator !== 'undefined' && navigator.sendBeacon?.(endpoint, new Blob([body], { type: 'text/plain' }))) return
      void fetch(endpoint, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain' } }).catch(() => undefined)
    } catch { /* reporting must never raise another error */ }
  }
}

/** Starts listening for unhandled errors. Returns a function that stops it. */
export function installClientErrorReporting(options: {
  target?: Pick<Window, 'addEventListener' | 'removeEventListener'>
  send: SendReport
  build: string
  surface: ClientErrorReport['surface']
  online?: () => boolean
  max?: number
}): () => void {
  const target = options.target ?? window
  const seen = new Set<string>()
  const max = options.max ?? MAX_REPORTS_PER_LOAD
  const submit = (kind: ClientErrorReport['kind'], reason: unknown) => {
    if (seen.size >= max || options.online?.() === false) return
    const report = buildClientErrorReport(kind, reason, options)
    const key = `${report.kind}|${report.name}|${report.message}|${report.frame}`
    if (seen.has(key)) return
    seen.add(key)
    options.send(report)
  }
  const onError = (event: Event) => { const e = event as ErrorEvent; submit('error', e.error ?? e.message) }
  const onRejection = (event: Event) => submit('unhandledrejection', (event as PromiseRejectionEvent).reason)
  target.addEventListener('error', onError)
  target.addEventListener('unhandledrejection', onRejection)
  return () => { target.removeEventListener('error', onError); target.removeEventListener('unhandledrejection', onRejection) }
}
